import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Card, CardContent } from "../ui/card";
import { Textarea } from "../ui/textarea";
import { RichTextEditor } from "../ui/RichTextEditor";
import {
  Plus, Trash2, GripVertical, Type, Image, Video, DollarSign, PenTool, Columns, Code, Link as LinkIcon, Eye, EyeOff, ArrowUp, ArrowDown, GitCompare
} from "lucide-react";
import { Switch } from "../ui/switch";
import { CoverPageDisplay } from "./CoverPageDisplay";
import { TextBlockEditor, ImageBlockEditor, HtmlBlockEditor as ContentHtmlBlockEditor, ColumnsBlockEditor as ContentColumnsBlockEditor, BeforeAfterBlockEditor } from "../quote-templates/ContentBlocks";
import { applyMargin } from "../../utils/pricing";
import { uploadVideoFile } from "../../lib/videoUpload";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

// =================== RICHTEXT BLOCK EDITOR ===================
function RichTextBlockEditor({ block, onChange, onRemove, onMoveUp, onMoveDown, isFirst, isLast }) {
  const html = typeof block.content === "object" ? (block.content?.html || "") : (block.content || "");
  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-white" data-testid={`block-richtext-${block.id}`}>
      <div className="flex items-center justify-between mb-2">
        <Badge variant="outline" className="capitalize text-xs"><Type className="w-3 h-3 mr-1" />Rich Text</Badge>
        <div className="flex gap-0.5">
          {!isFirst && <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={onMoveUp}><ArrowUp className="w-3 h-3" /></Button>}
          {!isLast && <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={onMoveDown}><ArrowDown className="w-3 h-3" /></Button>}
          <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400 hover:text-red-600" onClick={onRemove}><Trash2 className="w-3 h-3" /></Button>
        </div>
      </div>
      <RichTextEditor value={html} onChange={(val) => onChange({ ...block, content: { html: val } })} minHeight="120px" />
    </div>
  );
}


// =================== COVER PAGE EDITOR ===================

export function CoverPageEditor({ page, onChange, contact, project, settings }) {
  const content = page.content || {};
  const update = (k, v) => onChange({ ...content, [k]: v });
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Headline</Label>
        <Input value={content.headline || ""} onChange={(e) => update("headline", e.target.value)} className="h-12 text-lg font-semibold" placeholder="PROPOSAL" data-testid="cover-headline" />
      </div>
      <div className="space-y-2">
        <Label>Subheadline</Label>
        <Input value={content.subheadline || ""} onChange={(e) => update("subheadline", e.target.value)} className="h-12" placeholder="Prepared for:" />
      </div>
      <div className="flex flex-wrap gap-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={content.show_logo !== false} onChange={(e) => update("show_logo", e.target.checked)} className="w-4 h-4 accent-[#0F4C81]" />
          <span className="text-sm text-slate-600">Show Logo</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={content.show_date !== false} onChange={(e) => update("show_date", e.target.checked)} className="w-4 h-4 accent-[#0F4C81]" />
          <span className="text-sm text-slate-600">Show Date</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={content.show_quote_number !== false} onChange={(e) => update("show_quote_number", e.target.checked)} className="w-4 h-4 accent-[#0F4C81]" />
          <span className="text-sm text-slate-600">Show Quote #</span>
        </label>
      </div>
      {/* Live compact preview */}
      <CoverPageDisplay
        headline={content.headline || "PROPOSAL"}
        subheadline={content.subheadline || ""}
        logoUrl={content.logo_url || ""}
        bgColor={content.bgColor || "#0F4C81"}
        companyName={settings?.company_name || ""}
        contactName={contact?.name || ""}
        projectAddress={project?.project_address || ""}
        showLogo={content.show_logo !== false}
        showDate={content.show_date !== false}
        showQuoteNumber={content.show_quote_number !== false}
        compact={true}
      />
    </div>
  );
}

// =================== TEXT PAGE EDITOR ===================

export function TextPageEditor({ page, onChange }) {
  const content = page.content || {};
  const update = (k, v) => onChange({ ...content, [k]: v });
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Page Title</Label>
        <Input value={content.title || ""} onChange={(e) => update("title", e.target.value)} className="h-12" placeholder="Terms & Conditions" data-testid="text-page-title" />
      </div>
      <div className="space-y-2">
        <Label>Content</Label>
        <Textarea value={content.body || ""} onChange={(e) => update("body", e.target.value)} placeholder="Enter your terms, warranty info, FAQs..." rows={12} className="font-mono text-sm" data-testid="text-page-body" />
      </div>
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label>Sections</Label>
          <Button type="button" variant="outline" size="sm" onClick={() => {
            const sections = content.sections || [];
            update("sections", [...sections, { title: "", body: "" }]);
          }}><Plus className="w-4 h-4 mr-1" />Add Section</Button>
        </div>
        {(content.sections || []).map((section, i) => (
          <div key={i} className="p-3 border border-slate-200 rounded-lg space-y-2">
            <div className="flex gap-2">
              <Input placeholder="Section title" value={section.title} onChange={(e) => {
                const sections = [...(content.sections || [])];
                sections[i] = { ...sections[i], title: e.target.value };
                update("sections", sections);
              }} className="h-10" />
              <Button type="button" variant="ghost" size="sm" className="text-red-500" onClick={() => {
                const sections = (content.sections || []).filter((_, j) => j !== i);
                update("sections", sections);
              }}><Trash2 className="w-4 h-4" /></Button>
            </div>
            <Textarea placeholder="Section content..." value={section.body} onChange={(e) => {
              const sections = [...(content.sections || [])];
              sections[i] = { ...sections[i], body: e.target.value };
              update("sections", sections);
            }} rows={3} />
          </div>
        ))}
      </div>
    </div>
  );
}

// =================== COLUMNS BLOCK EDITOR ===================

function ColumnsBlockEditor({ block, onChange }) {
  const colCount = block.columns || 2;
  const colData = block.column_data || Array.from({ length: colCount }, () => ({ content: "" }));

  const setColCount = (n) => {
    const existing = [...colData];
    while (existing.length < n) existing.push({ content: "" });
    onChange({ ...block, columns: n, column_data: existing.slice(0, n) });
  };

  const updateCol = (idx, content) => {
    const updated = [...colData];
    updated[idx] = { ...updated[idx], content };
    onChange({ ...block, column_data: updated });
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <span className="text-xs text-slate-500">Layout:</span>
        <div className="flex items-center gap-1 border rounded bg-white">
          {[2, 3, 4].map(n => (
            <button key={n} type="button" onClick={() => setColCount(n)}
              className={`h-7 w-8 text-xs font-medium rounded ${colCount === n ? "bg-[#0F4C81] text-white" : "text-slate-500 hover:bg-slate-100"}`}
            >{n}</button>
          ))}
        </div>
        <span className="text-xs text-slate-400">columns</span>
      </div>
      <div className={`grid gap-3 ${colCount === 2 ? "grid-cols-2" : colCount === 3 ? "grid-cols-3" : "grid-cols-4"}`}>
        {colData.slice(0, colCount).map((col, ci) => (
          <div key={ci} className="space-y-1">
            <Label className="text-xs text-slate-500">Column {ci + 1}</Label>
            <Textarea value={col.content || ""} onChange={e => updateCol(ci, e.target.value)} rows={4} className="text-sm bg-white" placeholder={`Column ${ci + 1} content...`} />
          </div>
        ))}
      </div>
    </div>
  );
}

// =================== TABLE EDITOR ===================

function TableEditor({ value, onChange }) {
  const data = value || { headers: ["Column 1", "Column 2"], rows: [["", ""]] };
  const updateHeader = (i, val) => { const h = [...data.headers]; h[i] = val; onChange({ ...data, headers: h }); };
  const updateCell = (r, c, val) => { const rows = data.rows.map(row => [...row]); rows[r][c] = val; onChange({ ...data, rows }); };
  const addRow = () => onChange({ ...data, rows: [...data.rows, data.headers.map(() => "")] });
  const addCol = () => onChange({ headers: [...data.headers, `Column ${data.headers.length + 1}`], rows: data.rows.map(r => [...r, ""]) });
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr>
            {data.headers.map((h, i) => (
              <th key={i} className="p-1"><Input value={h} onChange={(e) => updateHeader(i, e.target.value)} className="h-8 text-xs font-semibold" /></th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.rows.map((row, ri) => (
            <tr key={ri}>
              {row.map((cell, ci) => (
                <td key={ci} className="p-1"><Input value={cell} onChange={(e) => updateCell(ri, ci, e.target.value)} className="h-8 text-xs" /></td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex gap-2 mt-2">
        <Button type="button" variant="outline" size="sm" onClick={addRow}>+ Row</Button>
        <Button type="button" variant="outline" size="sm" onClick={addCol}>+ Column</Button>
      </div>
    </div>
  );
}

// =================== HTML BLOCK EDITOR ===================

function HtmlBlockEditor({ block, onChange }) {
  const [showPreview, setShowPreview] = useState(false);
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-500 font-mono">Raw HTML</span>
        <Button type="button" variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={() => setShowPreview(!showPreview)}>
          {showPreview ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
          {showPreview ? "Edit" : "Preview"}
        </Button>
      </div>
      {showPreview ? (
        <div className="border border-slate-200 rounded-lg p-4 bg-white min-h-[100px]" dangerouslySetInnerHTML={{ __html: block.content || "" }} />
      ) : (
        <Textarea
          value={block.content || ""}
          onChange={(e) => onChange({ ...block, content: e.target.value })}
          rows={8}
          className="font-mono text-xs bg-slate-50"
          placeholder="<div>Your HTML here...</div>"
          data-testid="html-block-editor"
        />
      )}
    </div>
  );
}

// =================== CUSTOM PAGE EDITOR ===================

// =================== VIDEO BLOCK EDITOR ===================
/**
 * Supports two sources:
 *   - "url": YouTube, Vimeo, or direct .mp4 / .mov / .webm URL
 *   - "upload": file uploaded via /api/uploads (entity_type=quote-asset)
 *
 * The preview tries to detect YouTube/Vimeo for an iframe embed and falls
 * back to a native <video> tag for direct file URLs.
 */
function VideoBlockEditor({ block, onChange, onRemove, onMoveUp, onMoveDown, isFirst, isLast }) {
  const [uploading, setUploading] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const url = block.url || "";
  const caption = block.caption || "";

  // Recognise YouTube / Vimeo and turn into an embed URL
  const embedSrc = (() => {
    if (!url) return "";
    try {
      const u = new URL(url);
      if (u.hostname.includes("youtube.com") || u.hostname.includes("youtu.be")) {
        let videoId = u.searchParams.get("v");
        if (!videoId && u.hostname.includes("youtu.be")) {
          videoId = u.pathname.replace("/", "");
        }
        if (videoId) return `https://www.youtube.com/embed/${videoId}`;
      }
      if (u.hostname.includes("vimeo.com")) {
        const id = u.pathname.split("/").filter(Boolean).pop();
        if (id && /^\d+$/.test(id)) return `https://player.vimeo.com/video/${id}`;
      }
    } catch {}
    return "";
  })();
  const isDirectFile = url && !embedSrc;

  const handleUpload = async (file) => {
    if (!file) return;
    if (file.size > 200 * 1024 * 1024) {
      toast.error("Video is over 200MB — trim it or paste a YouTube/Vimeo link instead");
      return;
    }
    setUploading(true);
    try {
      const resp = { data: await uploadVideoFile(file, { entityType: "quote-asset", tag: "video", onProgress: setProgress }) };
      onChange({ ...block, url: `${process.env.REACT_APP_BACKEND_URL}${resp.data.url}` });
      toast.success("Video uploaded");
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      toast.error(status === 413 ? "Video too large for upload — paste a YouTube/Vimeo link instead" : `Upload failed${status ? ` (${status})` : ""}${detail ? `: ${detail}` : ""}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-white" data-testid={`block-video-${block.id}`}>
      <div className="flex items-center justify-between mb-2">
        <Badge variant="outline" className="capitalize text-xs"><Video className="w-3 h-3 mr-1" />Video</Badge>
        <div className="flex gap-0.5">
          {!isFirst && <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={onMoveUp}><ArrowUp className="w-3 h-3" /></Button>}
          {!isLast && <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={onMoveDown}><ArrowDown className="w-3 h-3" /></Button>}
          <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400 hover:text-red-600" onClick={onRemove}><Trash2 className="w-3 h-3" /></Button>
        </div>
      </div>
      <div className="space-y-2">
        <Input
          value={url}
          onChange={(e) => onChange({ ...block, url: e.target.value })}
          placeholder="Paste YouTube / Vimeo / direct video URL"
          className="h-10"
          data-testid={`block-video-url-${block.id}`}
        />
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <label className="inline-flex items-center gap-1 cursor-pointer hover:text-[#0F4C81]">
            <input
              type="file"
              accept="video/*"
              className="hidden"
              onChange={(e) => handleUpload(e.target.files?.[0])}
            />
            <Plus className="w-3.5 h-3.5" />
            {uploading ? `Uploading ${progress}%` : "Or upload a file (max 200MB)"}
          </label>
          {url && (
            <button type="button" className="text-red-400 hover:text-red-600 underline" onClick={() => onChange({ ...block, url: "" })}>
              clear
            </button>
          )}
        </div>
        <Input
          value={caption}
          onChange={(e) => onChange({ ...block, caption: e.target.value })}
          placeholder="Caption (optional)"
          className="h-9 text-sm"
        />
        {embedSrc && (
          <div className="aspect-video rounded-md overflow-hidden bg-black/5 border">
            <iframe
              src={embedSrc}
              title={caption || "Video preview"}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        )}
        {isDirectFile && (
          <video controls src={url} className="w-full rounded-md bg-black/5 border" preload="metadata" />
        )}
      </div>
    </div>
  );
}


export function CustomPageEditor({ page, onChange }) {
  const content = page.content || {};
  const update = (k, v) => onChange({ ...content, [k]: v });
  const blocks = content.blocks || [];

  const addBlock = (type) => {
    const base = { id: `block-${Date.now()}`, type, content: "", fontFamily: "Inter", fontSize: "14", color: "#1e293b", textAlign: "left", fontWeight: "normal", fontStyle: "normal", textDecoration: "none", height: 20, url: "", alt: "", label: "", width: "full" };
    if (type === "columns") {
      base.columns = 2;
      base.column_data = [{ content: "" }, { content: "" }];
    }
    if (type === "link") {
      base.label = "";
      base.url = "";
      base.new_tab = true;
      base.style = "primary";
    }
    if (type === "before_after") {
      base.before_url = "";
      base.after_url = "";
      base.before_label = "Before";
      base.after_label = "After";
      base.layout = "side_by_side";
      base.caption = "";
    }
    update("blocks", [...blocks, base]);
  };

  const updateBlock = (idx, updated) => {
    const arr = [...blocks];
    arr[idx] = updated;
    update("blocks", arr);
  };

  const removeBlock = (idx) => update("blocks", blocks.filter((_, i) => i !== idx));

  const moveBlock = (idx, dir) => {
    const arr = [...blocks];
    const target = idx + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    update("blocks", arr);
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Page Title</Label>
        <Input value={content.title || ""} onChange={(e) => update("title", e.target.value)} className="h-12" placeholder="Custom Page Title" />
      </div>
      <div className="flex gap-2 flex-wrap">
        <Button type="button" variant="outline" size="sm" onClick={() => addBlock("richtext")}><Type className="w-4 h-4 mr-1" />Rich Text</Button>
        <Button type="button" variant="outline" size="sm" onClick={() => addBlock("text")}><Type className="w-4 h-4 mr-1" />Plain Text</Button>
        <Button type="button" variant="outline" size="sm" onClick={() => addBlock("columns")}><Columns className="w-4 h-4 mr-1" />Columns</Button>
        <Button type="button" variant="outline" size="sm" onClick={() => addBlock("image")}><Image className="w-4 h-4 mr-1" />Image</Button>
        <Button type="button" variant="outline" size="sm" onClick={() => addBlock("video")}><Video className="w-4 h-4 mr-1" />Video</Button>
        <Button type="button" variant="outline" size="sm" onClick={() => addBlock("before_after")}><GitCompare className="w-4 h-4 mr-1" />Before & After</Button>
        <Button type="button" variant="outline" size="sm" onClick={() => addBlock("html")}><Code className="w-4 h-4 mr-1" />HTML</Button>
        <Button type="button" variant="outline" size="sm" onClick={() => addBlock("link")}><LinkIcon className="w-4 h-4 mr-1" />Link/Button</Button>
      </div>
      {blocks.map((block, i) => {
        const isFirst = i === 0;
        const isLast = i === blocks.length - 1;
        const commonProps = {
          block,
          onChange: (updated) => updateBlock(i, updated),
          onRemove: () => removeBlock(i),
          onMoveUp: () => moveBlock(i, -1),
          onMoveDown: () => moveBlock(i, 1),
          isFirst,
          isLast,
        };

        if (block.type === "text") return <TextBlockEditor key={block.id} {...commonProps} />;
        if (block.type === "richtext") return <RichTextBlockEditor key={block.id || i} {...commonProps} />;
        if (block.type === "image") return <ImageBlockEditor key={block.id} {...commonProps} />;
        if (block.type === "video") return <VideoBlockEditor key={block.id} {...commonProps} />;
        if (block.type === "before_after") return <BeforeAfterBlockEditor key={block.id} {...commonProps} />;
        if (block.type === "html") return <ContentHtmlBlockEditor key={block.id} {...commonProps} />;
        if (block.type === "columns") return <ContentColumnsBlockEditor key={block.id} {...commonProps} />;
        if (block.type === "link") return (
          <div key={block.id} className="border border-slate-200 rounded-lg p-3 bg-white">
            <div className="flex items-center justify-between mb-2">
              <Badge variant="outline" className="capitalize text-xs"><LinkIcon className="w-3 h-3 mr-1" />Link / Button</Badge>
              <div className="flex gap-0.5">
                {!isFirst && <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => moveBlock(i, -1)}><ArrowUp className="w-3 h-3" /></Button>}
                {!isLast && <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => moveBlock(i, 1)}><ArrowDown className="w-3 h-3" /></Button>}
                <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400 hover:text-red-600" onClick={() => removeBlock(i)}><Trash2 className="w-3 h-3" /></Button>
              </div>
            </div>
            <div className="space-y-2">
              <Input value={block.label || ""} onChange={(e) => updateBlock(i, { ...block, label: e.target.value })} placeholder="Button Label" className="h-9" />
              <Input value={block.url || ""} onChange={(e) => updateBlock(i, { ...block, url: e.target.value })} placeholder="URL (https://...)" className="h-9" />
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-1.5 text-xs">
                  <input type="checkbox" checked={block.new_tab !== false} onChange={(e) => updateBlock(i, { ...block, new_tab: e.target.checked })} className="rounded" />
                  Open in new tab
                </label>
                <select value={block.style || "primary"} onChange={(e) => updateBlock(i, { ...block, style: e.target.value })} className="h-8 text-xs border rounded px-2">
                  <option value="primary">Primary Button</option>
                  <option value="outline">Outline Button</option>
                  <option value="link">Text Link</option>
                </select>
              </div>
            </div>
          </div>
        );

        // Fallback for unknown types
        return (
          <div key={block.id} className="p-3 border rounded-lg border-slate-200">
            <div className="flex items-center justify-between mb-2">
              <Badge variant="outline" className="capitalize">{block.type}</Badge>
              <Button type="button" variant="ghost" size="sm" className="text-red-500" onClick={() => removeBlock(i)}><Trash2 className="w-4 h-4" /></Button>
            </div>
            <Textarea value={typeof block.content === 'string' ? block.content : JSON.stringify(block.content, null, 2)} onChange={(e) => updateBlock(i, { ...block, content: e.target.value })} rows={4} />
          </div>
        );
      })}
    </div>
  );
}

// =================== STRUCTURE PAGE EDITOR ===================

export function StructurePageEditor({ page, onChange, priceList }) {
  const content = page.content || {};
  const update = (k, v) => onChange({ ...content, [k]: v });
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Structure Name</Label>
        <Input value={content.title || ""} onChange={(e) => update("title", e.target.value)} className="h-12" placeholder="e.g., Main House, Garage, Detached Shed" />
      </div>
      <div className="space-y-2">
        <Label>Structure Notes</Label>
        <p className="text-xs text-slate-400">Important information that applies to all options under this structure. Visible to the customer.</p>
        <RichTextEditor
          value={content.notes || ""}
          onChange={(v) => update("notes", v)}
          rows={3}
          placeholder="e.g., Decking replacement billed separately at $75/sheet if damage found during tear-off..."
          testId="structure-notes-input"
        />
      </div>
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-700 font-medium">Options after this structure are grouped together. Customers pick one option per structure.</p>
        <p className="text-xs text-blue-600 mt-1">Line items here are <b>shared</b> across all options under this structure (e.g., tear-off, underlayment).</p>
      </div>
      <div className="space-y-2">
        <Label className="text-sm font-semibold text-slate-600">Shared Line Items</Label>
        <p className="text-xs text-slate-400">These items will be included in every option under this structure.</p>
        <StructureLineItemsEditor
          lineItems={content.line_items || []}
          structures={content.structures || []}
          priceList={priceList}
          taxRate={content.tax_rate || 0}
          onTaxRateChange={v => update("tax_rate", v)}
          onChange={({ line_items, structures }) => {
            onChange({ ...content, line_items, structures });
          }}
        />
      </div>
    </div>
  );
}

// =================== STRUCTURE LINE ITEMS EDITOR ===================

function StructureLineItemsEditor({ lineItems = [], structures = [], priceList = [], taxRate = 0, onTaxRateChange, onChange }) {
  const [showPicker, setShowPicker] = useState(false);
  const [pickerSearch, setPickerSearch] = useState("");
  const items = lineItems || [];

  const addItem = (priceItem) => {
    const newItem = {
      id: `li_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      price_list_id: priceItem.id, name: priceItem.name, unit_price: priceItem.base_price,
      margin: priceItem.margin || 0, quantity: 0, unit: priceItem.unit || "ea",
      visible: true, measurement_key: priceItem.measurement_key || "", conversion_factor: priceItem.conversion_factor || 1, waste_factor: priceItem.waste_factor || 0,
    };
    onChange({ line_items: [...items, newItem], structures });
    setShowPicker(false);
    setPickerSearch("");
  };
  const updateItem = (idx, field, value) => {
    const updated = [...items];
    updated[idx] = { ...updated[idx], [field]: value };
    onChange({ line_items: updated, structures });
  };
  const removeItem = (idx) => onChange({ line_items: items.filter((_, i) => i !== idx), structures });

  const calcItemTotal = (it) => {
    const qty = parseFloat(it.quantity) || 0;
    const price = parseFloat(it.unit_price) || 0;
    const margin = parseFloat(it.margin) || 0;
    const waste = parseFloat(it.waste_factor) || 0;
    return qty * (1 + waste / 100) * price * (1 + margin / 100);
  };
  const subtotal = items.reduce((sum, it) => sum + calcItemTotal(it), 0);

  const filteredPriceList = (priceList || []).filter(p =>
    p.enabled !== false && (p.name.toLowerCase().includes(pickerSearch.toLowerCase()) || p.category?.toLowerCase().includes(pickerSearch.toLowerCase()))
  );

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
      <div className="bg-slate-50 px-3 py-2 flex items-center justify-between border-b">
        <p className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Shared Items ({items.length})</p>
        <Button type="button" variant="outline" size="sm" className="h-7 text-xs bg-[#0F4C81] text-white hover:bg-[#0D4270] border-[#0F4C81]" onClick={() => setShowPicker(true)}>
          <Plus className="w-3 h-3 mr-1" />Add Item
        </Button>
      </div>
      {items.length > 0 ? (
        <div className="divide-y divide-slate-100">
          {items.map((it, idx) => (
            <div key={it.id || idx} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 group">
              <span className="text-sm flex-1 text-slate-700">{it.name}</span>
              <Input type="number" value={it.quantity ?? ''} onChange={e => updateItem(idx, "quantity", e.target.value === '' ? '' : parseFloat(e.target.value))} className="h-7 w-16 text-xs text-center" />
              <span className="text-[10px] text-slate-400">{it.unit || "ea"}</span>
              <span className="text-xs text-slate-400">@</span>
              <Input type="number" step="0.01" value={it.unit_price ?? ''} onChange={e => updateItem(idx, "unit_price", e.target.value === '' ? '' : parseFloat(e.target.value))} className="h-7 w-20 text-xs" />
              <span className="text-sm font-semibold text-slate-700 w-20 text-right">${calcItemTotal(it).toFixed(2)}</span>
              <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400 opacity-100 sm:opacity-0 sm:group-hover:opacity-100" onClick={() => removeItem(idx)}><Trash2 className="w-3 h-3" /></Button>
            </div>
          ))}
          <div className="px-3 py-2 bg-slate-50 flex justify-between text-sm font-semibold">
            <span className="text-slate-500">Shared Subtotal</span>
            <span className="text-slate-800">${subtotal.toFixed(2)}</span>
          </div>
        </div>
      ) : (
        <p className="text-xs text-slate-400 text-center py-4">No shared items. Add items that apply to all options under this structure.</p>
      )}
      {showPicker && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={() => { setShowPicker(false); setPickerSearch(""); }}>
          <div className="bg-white rounded-xl shadow-2xl w-[500px] max-h-[70vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b">
              <h3 className="text-sm font-bold text-slate-800 mb-2">Add Shared Item from Price List</h3>
              <Input value={pickerSearch} onChange={e => setPickerSearch(e.target.value)} placeholder="Search items..." className="h-9" autoFocus />
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {filteredPriceList.length > 0 ? filteredPriceList.map(p => (
                <button key={p.id} type="button" onClick={() => addItem(p)} className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-slate-50 text-left">
                  <div><p className="text-sm font-medium text-slate-700">{p.name}</p><p className="text-[10px] text-slate-400">{p.category} &middot; {p.unit || "ea"}</p></div>
                  <p className="text-sm font-semibold text-emerald-600">${p.base_price?.toFixed(2)}</p>
                </button>
              )) : <p className="text-sm text-slate-400 text-center py-8">{(priceList || []).length === 0 ? "No price list items yet." : "No matching items"}</p>}
            </div>
            <div className="p-3 border-t flex justify-end"><Button type="button" variant="outline" size="sm" onClick={() => { setShowPicker(false); setPickerSearch(""); }}>Close</Button></div>
          </div>
        </div>
      )}
    </div>
  );
}

// =================== OPTION PAGE EDITOR ===================

export function OptionPageEditor({ page, onChange, priceList, structureLineItems, profitMarginPct, vendorTaxPct }) {
  const content = page.content || {};
  const update = (k, v) => onChange({ ...content, [k]: v });

  // Auto-calculate from line items
  const optItems = content.line_items || [];
  const sharedItems = structureLineItems || [];
  const hasItems = optItems.length > 0;
  const optSubtotal = optItems.reduce((sum, it) => {
    const qty = parseFloat(it.quantity) || 0;
    const price = parseFloat(it.unit_price) || 0;
    const margin = parseFloat(it.margin) || 0;
    const waste = parseFloat(it.waste_factor) || 0;
    return sum + qty * (1 + waste / 100) * price * (1 + margin / 100);
  }, 0);
  const sharedSubtotal = sharedItems.reduce((sum, it) => {
    const qty = parseFloat(it.quantity) || 0;
    const price = parseFloat(it.unit_price) || 0;
    const margin = parseFloat(it.margin) || 0;
    const waste = parseFloat(it.waste_factor) || 0;
    return sum + qty * (1 + waste / 100) * price * (1 + margin / 100);
  }, 0);
  // Customer-facing total — matches Pricing tab, Preview, and Customer View formula:
  //   price = applyMargin(rawTotal, profit) + raw * vendorTax + per-option markup/adjust
  const totalRaw = optSubtotal + sharedSubtotal;
  const profitPct = parseFloat(profitMarginPct) || 0;
  const taxPct = parseFloat(vendorTaxPct) || 0;
  const optMkpPct = parseFloat(content.price_markup_pct) || 0;
  const optAdjVal = parseFloat(content.price_adjustment) || 0;
  const calcBase = applyMargin(totalRaw, profitPct) + totalRaw * (taxPct / 100);
  const calculatedTotal = calcBase + (calcBase * optMkpPct / 100) + optAdjVal;
  // Honour Base Price override (if user has typed a dollar amount in the Pricing field)
  const manualBase = parseFloat(content.base_price || content.manual_price) || 0;
  const sellsFor = manualBase > 0
    ? manualBase + (manualBase * optMkpPct / 100) + optAdjVal
    : calculatedTotal;

  // Categorize costs — prefer the linked Price List "category" (Material/Labor) when available,
  // and fall back to keyword matching on the item name.
  const priceMap = {};
  (priceList || []).forEach(pl => { priceMap[pl.id] = pl; });
  const isLaborItem = (it) => {
    const pl = it.price_list_id ? priceMap[it.price_list_id] : null;
    const cat = (pl?.category || "").toLowerCase();
    if (cat.includes("labor")) return true;
    if (cat.includes("material")) return false;
    const name = (it.name || "").toLowerCase();
    return name.includes("labor") || name.includes("install") || name.includes("tear");
  };
  const catCost = (items) => {
    let mat = 0, lab = 0;
    (items || []).forEach(it => {
      const qty = parseFloat(it.quantity) || 0;
      const price = parseFloat(it.unit_price) || 0;
      const waste = parseFloat(it.waste_factor) || 0;
      const cost = qty * (1 + waste / 100) * price;
      if (isLaborItem(it)) lab += cost;
      else mat += cost;
    });
    return { mat, lab };
  };
  const optCosts = catCost(optItems);
  const sharedCosts = catCost(sharedItems);
  const totalMat = optCosts.mat + sharedCosts.mat;
  const totalLab = optCosts.lab + sharedCosts.lab;
  const autoTotalCost = totalMat + totalLab;
  // Honour manual Cost Override (set on Preview tab "Private Profit Breakdown")
  const manualCost = parseFloat(content.cost_override) || 0;
  const totalCost = manualCost > 0 ? manualCost : autoTotalCost;
  const optMargin = sellsFor > 0 ? ((sellsFor - totalCost) / sellsFor * 100) : 0;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1"><Label className="text-xs font-semibold">Option Name *</Label><Input value={content.option_name || ""} onChange={e => update("option_name", e.target.value)} className="h-10" placeholder="e.g., IKO Dynasty Architectural" data-testid="option-name-input" /></div>
        <div className="space-y-1"><Label className="text-xs font-semibold">Option Tag</Label><Input value={content.option_tag || ""} onChange={e => update("option_tag", e.target.value)} className="h-10" placeholder="e.g., Most Affordable, Top Dog" data-testid="option-tag-input" /></div>
      </div>

      <div className="border border-slate-200 rounded-lg p-3 space-y-2">
        <div className="flex items-center gap-2 border-b border-slate-100 pb-2 mb-1">
          <div className="w-1 h-4 rounded bg-[#00A3E0]" />
          <Label className="text-xs font-bold uppercase text-[#00A3E0] tracking-wide">Highlights / Description</Label>
        </div>
        <RichTextEditor value={content.description || ""} onChange={v => update("description", v)} rows={3} placeholder="Key features, warranty info, selling points..." testId="option-description" />
      </div>

      <div className="border border-slate-200 rounded-lg p-3 space-y-2">
        <div className="flex items-center gap-2 border-b border-slate-100 pb-2 mb-1">
          <div className="w-1 h-4 rounded bg-amber-500" />
          <Label className="text-xs font-bold uppercase text-amber-600 tracking-wide">Scope of Work</Label>
        </div>
        <RichTextEditor value={content.scope || ""} onChange={v => update("scope", v)} rows={4} placeholder="1. Remove existing shingles&#10;2. Inspect decking&#10;3. Install underlayment..." testId="option-scope" />
      </div>

      <div className="border border-amber-200 rounded-lg p-3 bg-amber-50/30 space-y-2">
        <div className="flex items-center gap-2">
          <div className="w-1 h-4 rounded bg-amber-400" />
          <Label className="text-xs font-bold uppercase text-amber-600 tracking-wide">Important Information</Label>
        </div>
        <RichTextEditor value={content.important_info || ""} onChange={v => update("important_info", v)} rows={3} placeholder="Decking replacement policy, payment terms..." testId="option-important-info" />
      </div>

      <div className="border border-slate-200 rounded-lg p-3 space-y-2">
        <div className="flex items-center gap-2 border-b border-slate-100 pb-2 mb-1">
          <div className="w-1 h-4 rounded bg-emerald-500" />
          <Label className="text-xs font-bold uppercase text-emerald-600 tracking-wide">Pricing & Warranties</Label>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1"><Label className="text-xs">Warranty (Material)</Label><Input value={content.warranty_material || ""} onChange={e => update("warranty_material", e.target.value)} className="h-9 text-sm" placeholder="Limited Lifetime" /></div>
          <div className="space-y-1"><Label className="text-xs">Warranty (Wind)</Label><Input value={content.warranty_wind || ""} onChange={e => update("warranty_wind", e.target.value)} className="h-9 text-sm" placeholder="130 mph" /></div>
        </div>
        <p className="text-[11px] text-slate-500 italic mt-1">Price is set in the <span className="font-semibold text-[#0F4C81]">Private Profit Breakdown</span> card below.</p>
      </div>

      <div className="flex flex-wrap gap-4 pt-1">
        {[["show_price","Show Price"],["show_scope","Show Scope"],["show_warranty","Show Warranties"],["show_discounts","Apply Discounts"]].map(([k,l])=>(
          <label key={k} className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={content[k]!==false} onChange={e=>update(k,e.target.checked)} className="rounded" />{l}</label>
        ))}
      </div>

      <StructureLineItemsEditor
        lineItems={content.line_items || []}
        structures={content.structures || []}
        priceList={priceList}
        taxRate={content.tax_rate || 0}
        onTaxRateChange={v => update("tax_rate", v)}
        onChange={({ line_items, structures }) => {
          onChange({ ...content, line_items, structures });
        }}
      />

      {/* Proline-style Breakdown Cards — always shown so Pages tab mirrors Preview's profit panel */}
      <div className="grid grid-cols-3 gap-3">
          <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
            <p className="text-[10px] uppercase text-slate-400 font-bold mb-2">Costs by Type</p>
            <div className="space-y-1.5">
              <div className="flex justify-between text-xs">
                <span className="text-blue-600 font-semibold">Material</span>
                <span className="font-semibold">${totalMat.toFixed(2)}</span>
              </div>
              {sharedItems.length > 0 && (totalMat > 0) && (
                <div className="flex justify-between text-[10px] text-slate-400 pl-3">
                  <span>option ${optCosts.mat.toFixed(2)} + structure ${sharedCosts.mat.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-xs">
                <span className="text-orange-600 font-semibold">Labor</span>
                <span className="font-semibold">${totalLab.toFixed(2)}</span>
              </div>
              {sharedItems.length > 0 && (totalLab > 0) && (
                <div className="flex justify-between text-[10px] text-slate-400 pl-3">
                  <span>option ${optCosts.lab.toFixed(2)} + structure ${sharedCosts.lab.toFixed(2)}</span>
                </div>
              )}
            </div>
          </div>
          <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
            <p className="text-[10px] uppercase text-slate-400 font-bold mb-2">Price Breakdown</p>
            <div className="space-y-1.5">
              <div className="flex justify-between text-xs">
                <span className="text-slate-600">Option Items</span>
                <span className="font-semibold">${optSubtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-slate-600">Structure Items</span>
                <span className="font-semibold">${sharedSubtotal.toFixed(2)}</span>
              </div>
              {profitPct > 0 && (
                <div className="flex justify-between text-xs">
                  <span className="text-emerald-600">+ Margin ({profitPct}%)</span>
                  <span className="font-semibold text-emerald-600">${(applyMargin(totalRaw, profitPct) - totalRaw).toFixed(2)}</span>
                </div>
              )}
              {taxPct > 0 && (
                <div className="flex justify-between text-xs">
                  <span className="text-slate-500">+ Vendor Tax ({taxPct}%)</span>
                  <span className="font-semibold">${(totalRaw * taxPct / 100).toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-xs border-t pt-1 font-bold">
                <span className="text-[#0F4C81]">Customer Total</span>
                <span className="text-[#0F4C81]">${calculatedTotal.toFixed(2)}</span>
              </div>
            </div>
          </div>
          <div className="bg-amber-50/70 rounded-lg p-3 border border-amber-200">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] uppercase text-amber-700 font-bold tracking-widest">Private Profit Breakdown</p>
              <span className="text-[9px] text-amber-700/70 italic">Hidden from customer</span>
            </div>
            <div className="space-y-1.5">
              <div>
                <label className="text-[10px] text-slate-500 uppercase tracking-wide">Your Cost</label>
                <input
                  type="number"
                  step="1"
                  value={content.cost_override ?? ""}
                  onChange={(e) => update("cost_override", e.target.value === "" ? "" : parseFloat(e.target.value))}
                  placeholder={autoTotalCost.toFixed(2)}
                  className="w-full h-7 rounded border border-slate-200 bg-white px-2 text-xs font-semibold text-slate-700 focus:outline-none focus:ring-1 focus:ring-amber-400"
                  title={autoTotalCost > 0 ? `Auto-calc: $${autoTotalCost.toFixed(2)}. Type to override.` : "Enter your actual cost"}
                  data-testid="pages-option-cost-override"
                />
              </div>
              <div>
                <label className="text-[10px] text-slate-500 uppercase tracking-wide">Sells For</label>
                <input
                  type="number"
                  step="1"
                  value={content.base_price ?? content.manual_price ?? ""}
                  onChange={(e) => update("base_price", e.target.value === "" ? "" : parseFloat(e.target.value))}
                  placeholder={calculatedTotal.toFixed(2)}
                  className="w-full h-7 rounded border border-[#0F4C81]/30 bg-white px-2 text-xs font-bold text-[#0F4C81] focus:outline-none focus:ring-1 focus:ring-[#0F4C81]"
                  title="Price customer pays. Flows to Pricing tab, Preview, and customer view."
                  data-testid="pages-option-sells-for"
                />
              </div>
              <div className="flex justify-between items-center border-t border-slate-200 pt-1 text-xs">
                <span className={`font-bold ${optMargin >= 30 ? "text-emerald-600" : optMargin >= 15 ? "text-amber-600" : "text-red-600"}`}>Margin</span>
                <span className={`font-bold ${optMargin >= 30 ? "text-emerald-600" : optMargin >= 15 ? "text-amber-600" : "text-red-600"}`}>
                  ${(sellsFor - totalCost).toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})} · {optMargin.toFixed(1)}%
                </span>
              </div>
            </div>
          </div>
        </div>
    </div>
  );
}

// =================== ADDON PAGE EDITOR ===================

export function AddonPageEditor({ page, onChange, priceList }) {
  const content = page.content || {};
  const update = (k, v) => onChange({ ...content, [k]: v });
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1"><Label className="text-xs">Add-on Name</Label><Input value={content.addon_name || ""} onChange={e => update("addon_name", e.target.value)} className="h-10" placeholder="e.g., Extended Warranty Upgrade" data-testid="addon-name-input" /></div>
        <div className="space-y-1"><Label className="text-xs">Sells For ($)</Label><Input type="number" value={content.base_price ?? content.price ?? ""} onChange={e => update("base_price", e.target.value === "" ? "" : parseFloat(e.target.value))} className="h-10 font-bold text-[#0F4C81] border-[#0F4C81]/30" placeholder="0.00" data-testid="addon-price-input" title="Price shown to customer on View & Sign. Syncs to Pricing tab and customer view." /></div>
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Description</Label>
        <RichTextEditor value={content.description || ""} onChange={v => update("description", v)} rows={3} placeholder="What this add-on includes..." testId="addon-description" />
      </div>
      <div className="flex gap-4 flex-wrap">
        <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={content.show_price!==false} onChange={e=>update("show_price",e.target.checked)} className="rounded" />Show Price</label>
        <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={content.apply_discount===true} onChange={e=>update("apply_discount",e.target.checked)} className="rounded" />Apply Discount</label>
        <label className="flex items-center gap-1.5 text-xs" title="If on, this add-on starts checked when the customer opens the quote. They can still uncheck it before signing.">
          <input type="checkbox" checked={content.default_selected===true} onChange={e=>update("default_selected",e.target.checked)} className="rounded" data-testid="addon-default-selected" />Selected by default
        </label>
      </div>
      <StructureLineItemsEditor
        lineItems={content.line_items || []}
        structures={content.structures || []}
        priceList={priceList}
        taxRate={content.tax_rate || 0}
        onTaxRateChange={v => update("tax_rate", v)}
        onChange={({ line_items, structures }) => {
          onChange({ ...content, line_items, structures });
        }}
      />
    </div>
  );
}



// =================== FINANCING PAGE EDITOR ===================

export function FinancingPageEditor({ page, onChange }) {
  const content = page.content || {};
  const update = (k, v) => onChange({ ...content, [k]: v });
  
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label className="text-xs font-bold uppercase text-slate-500 tracking-wide">Intro Text</Label>
        <Textarea
          value={content.intro || ""}
          onChange={e => update("intro", e.target.value)}
          placeholder="We've partnered with trusted lenders..."
          rows={3}
          data-testid="financing-intro"
        />
      </div>

      {/* HFS Financial */}
      <Card className="border-emerald-200">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-emerald-500" />
            <p className="font-bold text-emerald-700 text-sm">HFS Financial</p>
            <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Funded to Homeowner</Badge>
          </div>
          <div className="space-y-2">
            <Label className="text-xs text-slate-500">Description</Label>
            <Textarea
              value={content.hfs_desc || ""}
              onChange={e => update("hfs_desc", e.target.value)}
              placeholder="Loan funds are deposited directly into your bank account."
              rows={2}
              data-testid="financing-hfs-desc"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs text-slate-500">Pre-Qualification Link</Label>
            <Input
              value={content.hfs_link || ""}
              onChange={e => update("hfs_link", e.target.value)}
              placeholder="https://app.hfs.com/prequal/..."
              data-testid="financing-hfs-link"
            />
          </div>
        </CardContent>
      </Card>

      {/* Wisetack */}
      <Card className="border-blue-200">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-[#0F4C81]" />
            <p className="font-bold text-[#0F4C81] text-sm">Wisetack</p>
            <Badge className="bg-blue-100 text-blue-700 text-[10px]">Funded to Contractor</Badge>
          </div>
          <div className="space-y-2">
            <Label className="text-xs text-slate-500">Description</Label>
            <Textarea
              value={content.wisetack_desc || ""}
              onChange={e => update("wisetack_desc", e.target.value)}
              placeholder="Funds go directly to your contractor. Easy monthly payments."
              rows={2}
              data-testid="financing-wisetack-desc"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs text-slate-500">Pre-Qualification Link</Label>
            <Input
              value={content.wisetack_link || ""}
              onChange={e => update("wisetack_link", e.target.value)}
              placeholder="https://wisetack.us/..."
              data-testid="financing-wisetack-link"
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={content.show_calculator !== false}
              onChange={e => update("show_calculator", e.target.checked)}
              className="w-4 h-4 accent-[#0F4C81]"
              data-testid="financing-show-calculator"
            />
            <span className="text-sm text-slate-600">Show Payment Calculator</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
